/**
 * Mobile-first voice-to-cart component for the Client Portal.
 * ----------------------------------------------------------------
 * Tap the mic → record → tap again to stop.
 * Sends the recorded audio to POST /portal/voice/parse where Whisper transcribes
 * (auto-detects Hindi / English / Hinglish / Gujarati) and Gemini structures
 * the transcript into cart line items + custom items.  A confirm-preview modal
 * lets the client review before anything hits the cart.
 *
 * Cheapest SKU wins when brand is ambiguous (per user's decision).  Anything
 * the parser can't match to the item master lands as a "custom item" — same
 * flow as the existing typed quick-add row.
 */
import { useEffect, useImperativeHandle, useRef, useState, forwardRef } from "react";
import { Mic, Square, Loader2, X, Check, Sparkles, AlertTriangle,
         Volume2, Plus, Minus } from "lucide-react";
import { toast } from "sonner";
import portalApi from "@/lib/portalApi";

const LANG_LABEL = {
    hi: "Hindi", en: "English", hinglish: "Hinglish",
    gu: "Gujarati", mr: "Marathi", "": "auto",
};

const PortalVoiceCart = forwardRef(function PortalVoiceCart(
    { catalog, onAddToCart, onAddCustom, showFloating = false }, ref
) {
    const [state, setState] = useState("idle");   // idle · recording · uploading · preview · error
    const [error, setError] = useState("");
    const [result, setResult] = useState(null);   // {transcript, language, matched, custom_items}
    const [level, setLevel] = useState(0);        // 0..1 live mic level for waveform
    const [elapsed, setElapsed] = useState(0);    // seconds recording
    const recorderRef = useRef(null);
    const chunksRef = useRef([]);
    const streamRef = useRef(null);
    const analyserRef = useRef(null);
    const rafRef = useRef(null);
    const startedAtRef = useRef(0);
    const timerRef = useRef(null);

    // Cleanup on unmount
    useEffect(() => () => stopAllStreams(), []);

    // Expose imperative `start()` so a parent-rendered button (e.g. the
    // "Speak your order" banner) can kick off recording without owning any
    // audio state itself.
    useImperativeHandle(ref, () => ({
        start: () => { if (state === "idle" || state === "error") start(); },
        stop: () => { if (state === "recording") stop(); },
        state,
    }), [state]);

    const stopAllStreams = () => {
        try { recorderRef.current?.state === "recording" && recorderRef.current.stop(); } catch { /* noop */ }
        try { streamRef.current?.getTracks().forEach(t => t.stop()); } catch { /* noop */ }
        cancelAnimationFrame(rafRef.current);
        clearInterval(timerRef.current);
        streamRef.current = null; analyserRef.current = null;
    };

    const start = async () => {
        setError(""); setResult(null);
        if (!navigator.mediaDevices?.getUserMedia) {
            setError("Your browser doesn't support microphone access.");
            setState("error"); return;
        }
        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                audio: { echoCancellation: true, noiseSuppression: true, channelCount: 1 },
            });
            streamRef.current = stream;

            // Pick the best mime type the browser supports. Android Chrome does
            // audio/webm;codecs=opus; iOS Safari does audio/mp4.
            const mimeCandidates = [
                "audio/webm;codecs=opus", "audio/webm",
                "audio/mp4", "audio/mpeg", "audio/ogg;codecs=opus",
            ];
            const mime = mimeCandidates.find(m => window.MediaRecorder?.isTypeSupported?.(m)) || "";
            const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
            recorderRef.current = rec;
            chunksRef.current = [];
            rec.ondataavailable = (e) => e.data.size > 0 && chunksRef.current.push(e.data);
            rec.onstop = async () => {
                const blob = new Blob(chunksRef.current, { type: mime || "audio/webm" });
                const ext = (mime.includes("mp4") ? "m4a"
                            : mime.includes("mpeg") ? "mp3"
                            : mime.includes("ogg") ? "ogg" : "webm");
                await upload(blob, ext);
                stopAllStreams();
            };
            rec.start(250);   // 250 ms chunks → smoother stop, faster UI

            // Live level meter for the waveform
            const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
            const src = audioCtx.createMediaStreamSource(stream);
            const analyser = audioCtx.createAnalyser();
            analyser.fftSize = 512;
            src.connect(analyser);
            analyserRef.current = analyser;
            const data = new Uint8Array(analyser.frequencyBinCount);
            const tick = () => {
                if (!analyserRef.current) return;
                analyser.getByteFrequencyData(data);
                let sum = 0;
                for (let i = 0; i < data.length; i++) sum += data[i];
                setLevel(Math.min(1, (sum / data.length) / 96));
                rafRef.current = requestAnimationFrame(tick);
            };
            tick();

            startedAtRef.current = Date.now();
            setElapsed(0);
            timerRef.current = setInterval(() => {
                const s = Math.floor((Date.now() - startedAtRef.current) / 1000);
                setElapsed(s);
                // Hard cap at 25 sec to keep Whisper fast + costs low
                if (s >= 25) stop();
            }, 500);

            // Haptic buzz on Android
            navigator.vibrate?.(15);
            setState("recording");
        } catch (e) {
            setError(e.name === "NotAllowedError"
                ? "Microphone permission was denied. Please enable mic access for HDB in your browser settings."
                : (e.message || "Could not start recording"));
            setState("error");
        }
    };

    const stop = () => {
        try { recorderRef.current?.state === "recording" && recorderRef.current.stop(); }
        catch { /* noop */ }
        navigator.vibrate?.(10);
        setState("uploading");
    };

    const upload = async (blob, ext) => {
        try {
            const fd = new FormData();
            fd.append("audio", blob, `voice.${ext}`);
            const r = await portalApi.post("/voice/parse", fd, {
                headers: { "Content-Type": "multipart/form-data" },
                timeout: 60_000,
            });
            setResult(r.data);
            setState("preview");
        } catch (e) {
            const msg = e.response?.data?.detail || e.message || "Voice request failed";
            setError(msg);
            setState("error");
            toast.error(msg);
        }
    };

    const closeAll = () => {
        stopAllStreams();
        setState("idle"); setResult(null); setError(""); setElapsed(0);
    };

    // Only render the floating mic when the page hands us a catalog with ≥1 item.
    const hasCatalog = (catalog?.categories?.length ?? 0) > 0;
    if (!hasCatalog) return null;

    return (
        <>
            {/* Optional floating mic — hidden by default now that the banner
                is the primary trigger. Enable via `showFloating` prop if a
                secondary access point is desired. */}
            {showFloating && (
            <button
                onClick={state === "idle" || state === "error" ? start : stop}
                className={`fixed z-30 bottom-6 right-5 md:bottom-8 md:right-8
                    w-16 h-16 md:w-14 md:h-14 rounded-full shadow-2xl grid place-items-center
                    text-white transition-transform active:scale-95
                    ${state === "recording"
                        ? "bg-red-600 hover:bg-red-700 animate-pulse ring-4 ring-red-300/50"
                        : state === "uploading"
                        ? "bg-slate-500"
                        : "bg-gradient-to-br from-emerald-600 to-emerald-700 hover:from-emerald-500 hover:to-emerald-600 ring-4 ring-emerald-200/60"}`}
                data-testid="portal-voice-mic"
                aria-label={state === "recording" ? "Stop recording" : "Speak to build your cart"}
                disabled={state === "uploading"}
            >
                {state === "recording"
                    ? <Square size={22} className="fill-white" />
                    : state === "uploading"
                    ? <Loader2 size={22} className="animate-spin" />
                    : <Mic size={26} />}
            </button>
            )}

            {/* Recording sheet (bottom) */}
            {(state === "recording" || state === "uploading") && (
                <div className="fixed inset-x-0 bottom-0 z-40 bg-white border-t border-slate-200 shadow-2xl rounded-t-3xl p-5 pb-8"
                     data-testid="portal-voice-recording">
                    <div className="flex items-center gap-3 mb-4">
                        <div className={`w-10 h-10 rounded-full grid place-items-center
                                        ${state === "recording" ? "bg-red-100 text-red-600" : "bg-slate-100 text-slate-600"}`}>
                            {state === "recording" ? <Mic size={18}/> : <Loader2 size={18} className="animate-spin"/>}
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="font-bold text-slate-900">
                                {state === "recording" ? "Listening…" : "Understanding what you said…"}
                            </div>
                            <div className="text-xs text-slate-500">
                                {state === "recording"
                                    ? "Bolte raho — jab ho jaaye tab red button dabaao. (Hindi · Gujarati · English · Hinglish ok)"
                                    : "Transcribing & matching to catalog"}
                            </div>
                        </div>
                        <div className="font-mono text-slate-600 text-sm">{fmtElapsed(elapsed)}</div>
                    </div>

                    {/* Live waveform (simple bars) */}
                    <div className="flex items-center justify-center h-14 gap-1">
                        {Array.from({ length: 24 }).map((_, i) => {
                            const wave = state === "recording"
                                ? Math.max(0.15, Math.min(1, level * (1 + Math.sin(i * 0.7 + Date.now() / 120))))
                                : 0.25;
                            return (
                                <div key={i}
                                     style={{ height: `${18 + wave * 40}px`,
                                              opacity: 0.5 + wave * 0.5 }}
                                     className={`w-1.5 rounded-full transition-all
                                                ${state === "recording" ? "bg-red-500" : "bg-slate-300"}`}
                                />
                            );
                        })}
                    </div>

                    {state === "recording" && (
                        <button onClick={stop}
                                className="mt-4 w-full py-4 rounded-2xl bg-red-600 text-white font-black text-lg shadow-lg active:scale-[0.98]"
                                data-testid="portal-voice-stop">
                            <Square size={16} className="inline mr-2 fill-white"/> Stop & Build Cart
                        </button>
                    )}
                </div>
            )}

            {/* Preview modal */}
            {state === "preview" && result && (
                <VoicePreview result={result}
                              onCancel={closeAll}
                              onConfirm={(picked, customs) => {
                                  picked.forEach(m => onAddToCart(m));
                                  customs.forEach(c => onAddCustom(c));
                                  toast.success(`Added ${picked.length + customs.length} item${picked.length + customs.length !== 1 ? "s" : ""} to cart`);
                                  closeAll();
                              }} />
            )}

            {/* Error dialog */}
            {state === "error" && (
                <div className="fixed inset-0 z-50 bg-slate-900/70 flex items-end md:items-center justify-center p-4"
                     onClick={closeAll}>
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-5"
                         onClick={e => e.stopPropagation()}
                         data-testid="portal-voice-error">
                        <div className="flex items-start gap-3">
                            <div className="w-10 h-10 rounded-full bg-red-100 text-red-600 grid place-items-center shrink-0">
                                <AlertTriangle size={18}/>
                            </div>
                            <div className="flex-1">
                                <h3 className="font-black text-slate-900">Voice recording failed</h3>
                                <p className="text-sm text-slate-600 mt-1">{error}</p>
                            </div>
                        </div>
                        <div className="mt-4 flex justify-end gap-2">
                            <button onClick={closeAll}
                                    className="px-4 py-2 rounded-lg bg-slate-100 text-slate-700 font-bold text-sm"
                                    data-testid="portal-voice-error-close">Close</button>
                            <button onClick={() => { setState("idle"); start(); }}
                                    className="px-4 py-2 rounded-lg bg-slate-900 text-white font-bold text-sm">
                                Try again
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
});

export default PortalVoiceCart;


/* ------------------------------ Preview Modal ------------------------------ */
function VoicePreview({ result, onCancel, onConfirm }) {
    // Local state so the user can uncheck items or tweak qty before adding
    const [rows, setRows] = useState(() =>
        (result.matched || []).map(m => ({ ...m, picked: true })));
    const [customs, setCustoms] = useState(() =>
        (result.custom_items || []).map(c => ({ ...c, picked: true })));

    const toggle = (i) => setRows(rs => rs.map((r, idx) => idx === i ? { ...r, picked: !r.picked } : r));
    const toggleC = (i) => setCustoms(cs => cs.map((c, idx) => idx === i ? { ...c, picked: !c.picked } : c));
    const bumpQty = (i, delta) => setRows(rs => rs.map((r, idx) =>
        idx === i ? { ...r, qty: Math.max(0.1, +(r.qty + delta).toFixed(2)) } : r));
    const bumpQtyC = (i, delta) => setCustoms(cs => cs.map((c, idx) =>
        idx === i ? { ...c, qty: Math.max(0.1, +((c.qty || 1) + delta).toFixed(2)) } : c));
    const swapBrand = (i, alt) => setRows(rs => rs.map((r, idx) => {
        if (idx !== i) return r;
        return { ...r, item_id: alt.item_id, name: alt.name, rate: alt.rate,
                 unit: alt.unit || r.unit,
                 alternatives: [{ item_id: r.item_id, name: r.name, rate: r.rate, unit: r.unit },
                                ...r.alternatives.filter(a => a.item_id !== alt.item_id)] };
    }));

    const nothing = rows.length === 0 && customs.length === 0;
    const doConfirm = () => onConfirm(rows.filter(r => r.picked),
                                     customs.filter(c => c.picked));

    return (
        <div className="fixed inset-0 z-50 bg-slate-900/70 flex items-end md:items-center justify-center p-0 md:p-4"
             onClick={onCancel} data-testid="portal-voice-preview">
            <div className="bg-white w-full max-w-xl md:rounded-2xl rounded-t-3xl shadow-2xl max-h-[92vh] overflow-hidden flex flex-col"
                 onClick={e => e.stopPropagation()}>
                {/* Header */}
                <div className="p-4 border-b border-slate-200 flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-emerald-100 text-emerald-700 grid place-items-center shrink-0">
                        <Sparkles size={18}/>
                    </div>
                    <div className="flex-1 min-w-0">
                        <h2 className="text-lg font-black text-slate-900" style={{fontFamily:"'Chivo',sans-serif"}}>
                            Here's what I heard
                        </h2>
                        <p className="text-xs text-slate-500 mt-0.5 flex items-center gap-1.5">
                            <Volume2 size={11}/> {LANG_LABEL[result.language] || result.language || "auto"} · tap items you don't want
                        </p>
                    </div>
                    <button onClick={onCancel} className="p-1 hover:bg-slate-100 rounded"
                            data-testid="portal-voice-cancel"><X size={18}/></button>
                </div>

                {/* Transcript pill */}
                {result.transcript && (
                    <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-200">
                        <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">You said</div>
                        <div className="text-sm text-slate-800 italic">"{result.transcript}"</div>
                    </div>
                )}

                {/* Items */}
                <div className="flex-1 overflow-y-auto p-3 space-y-2">
                    {nothing && (
                        <div className="text-center py-10 text-slate-500">
                            <AlertTriangle className="mx-auto mb-2 text-amber-500" size={28}/>
                            <div className="font-bold">Nothing recognised</div>
                            <div className="text-xs mt-1">
                                Try again — say something like <br/>
                                <span className="italic">"do kilo basmati chawal, ek packet dhaniya"</span>
                            </div>
                        </div>
                    )}

                    {rows.map((r, i) => (
                        <MatchedRow key={i} row={r} idx={i}
                                    onToggle={() => toggle(i)}
                                    onBump={(d) => bumpQty(i, d)}
                                    onSwap={(alt) => swapBrand(i, alt)} />
                    ))}

                    {customs.length > 0 && (
                        <div className="pt-2">
                            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-2 px-1">
                                Custom items (HDB will confirm price)
                            </div>
                            {customs.map((c, i) => (
                                <div key={i}
                                     className={`p-2.5 rounded-xl border mb-1.5 flex items-center gap-2
                                                 ${c.picked ? "bg-amber-50 border-amber-200" : "bg-slate-50 border-slate-200 opacity-50"}`}
                                     data-testid={`portal-voice-custom-${i}`}>
                                    <input type="checkbox" checked={c.picked} onChange={() => toggleC(i)}
                                           className="w-5 h-5 accent-emerald-600"/>
                                    <div className="flex-1 min-w-0">
                                        <div className="font-bold text-sm text-slate-900 truncate">{c.name}</div>
                                        <div className="text-[10px] text-slate-500">Heard: "{c.spoken_as}"</div>
                                    </div>
                                    <QtyPicker qty={c.qty || 1} unit={c.unit || "packet"}
                                               onBump={(d) => bumpQtyC(i, d)}/>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="p-4 border-t border-slate-200 flex gap-2 bg-slate-50">
                    <button onClick={onCancel}
                            className="flex-1 py-3 rounded-xl bg-white border border-slate-300 text-slate-700 font-bold"
                            data-testid="portal-voice-preview-cancel">
                        Cancel
                    </button>
                    <button onClick={doConfirm}
                            disabled={nothing}
                            className={`flex-[2] py-3 rounded-xl font-black text-white shadow-lg
                                        ${nothing ? "bg-slate-300" : "bg-emerald-600 hover:bg-emerald-700 active:scale-[0.98]"}`}
                            data-testid="portal-voice-preview-confirm">
                            <Check size={16} className="inline mr-1"/>
                            Add {rows.filter(r => r.picked).length + customs.filter(c => c.picked).length} to cart
                    </button>
                </div>
            </div>
        </div>
    );
}


function MatchedRow({ row, idx, onToggle, onBump, onSwap }) {
    const conf = row.match_confidence || 0;
    const confBadge = conf >= 0.85 ? "bg-emerald-100 text-emerald-800"
                     : conf >= 0.65 ? "bg-amber-100 text-amber-800"
                     : "bg-slate-100 text-slate-700";
    return (
        <div className={`p-3 rounded-xl border transition-colors
                         ${row.picked ? "bg-white border-emerald-200" : "bg-slate-50 border-slate-200 opacity-50"}`}
             data-testid={`portal-voice-row-${idx}`}>
            <div className="flex items-center gap-2">
                <input type="checkbox" checked={row.picked} onChange={onToggle}
                       className="w-5 h-5 accent-emerald-600 shrink-0"/>
                <div className="flex-1 min-w-0">
                    <div className="font-bold text-sm text-slate-900 truncate"
                         data-testid={`portal-voice-row-name-${idx}`}>
                        {row.name}
                    </div>
                    <div className="text-[10px] text-slate-500 flex items-center gap-1.5">
                        <span className={`px-1.5 py-0.5 rounded font-mono font-bold ${confBadge}`}>
                            {Math.round(conf * 100)}% match
                        </span>
                        <span>· heard: "{row.spoken_as}"</span>
                    </div>
                </div>
                <QtyPicker qty={row.qty} unit={row.unit} onBump={onBump}/>
                <div className="text-right shrink-0 min-w-[64px]">
                    <div className="font-mono font-bold text-slate-900 text-sm">
                        ₹{(row.rate * row.qty).toFixed(0)}
                    </div>
                    <div className="text-[10px] text-slate-400">
                        @ ₹{row.rate}/{row.unit}
                    </div>
                </div>
            </div>
            {/* Brand swap chips */}
            {row.alternatives?.length > 0 && (
                <div className="mt-2 flex items-center gap-1 flex-wrap pl-7">
                    <span className="text-[10px] text-slate-400 uppercase tracking-wider mr-1">or:</span>
                    {row.alternatives.slice(0, 3).map((alt, ai) => (
                        <button key={ai} onClick={() => onSwap(alt)}
                                className="text-[11px] px-2 py-0.5 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700"
                                data-testid={`portal-voice-row-alt-${idx}-${ai}`}>
                            {alt.name.length > 24 ? alt.name.slice(0, 22) + "…" : alt.name}
                            {" "}<span className="text-slate-400">₹{alt.rate}</span>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}


function QtyPicker({ qty, unit, onBump }) {
    return (
        <div className="flex items-center gap-1.5 shrink-0">
            <button onClick={() => onBump(unit === "kg" || unit === "l" ? -0.5 : -1)}
                    className="w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 grid place-items-center">
                <Minus size={13}/>
            </button>
            <div className="text-center min-w-[50px]">
                <div className="font-mono font-black text-sm">{qty}</div>
                <div className="text-[9px] text-slate-500 uppercase">{unit}</div>
            </div>
            <button onClick={() => onBump(unit === "kg" || unit === "l" ? 0.5 : 1)}
                    className="w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 grid place-items-center">
                <Plus size={13}/>
            </button>
        </div>
    );
}


function fmtElapsed(s) {
    const mm = Math.floor(s / 60).toString().padStart(2, "0");
    const ss = (s % 60).toString().padStart(2, "0");
    return `${mm}:${ss}`;
}
